import { bbox } from '@turf/turf';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import type { Feature, FeatureCollection, GeoJsonProperties, Point } from 'geojson';
import type { CirclePaint, GeoJSONSource, LngLatBoundsLike } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import { REGIONAL_OVERVIEW_MAP_PADDING } from '@features/regional-overview/regional-overview.constants';
import type { RegionalOverviewProperties } from 'querys/regional-overview/regional-overview.query.models';
import { useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useSelector } from 'react-redux';
import type { AppState } from 'redux/app-state';
import RegionalOverviewMycotoxinFieldsInfoPopupContainer from '../regional-overview-mycotoxin-fields-info-popup-container/regional-overview-mycotoxin-fields-info-popup-container';
import { RegionalRiskMycotoxinRiskLevelColors } from '../regional-overview-mycotoxin.models';

interface RenderMycotoxinMap<T extends GeoJsonProperties> {
  map?: mapboxgl.Map;
  data?: FeatureCollection<Point, T>;
}
interface RenderMap {
  hasRendered: boolean;
}

export const POINTS_DATA_SOURCE = 'data-source';
export const POINTS_LAYER = 'data-points-layer';
export const POINTS_OVERVIEW_LAYER = 'data-overview-points-layer';
export const POINTS_CLUSTER_DATA_SOURCE = 'data-cluster-source';
export const POINTS_CLUSTER_COUNTER_LAYER = 'points-layer-cluster-counter';
export const POINTS_CLUSTER_CIRCLE_LAYER = 'points-layer-cluster-circle-layer';

function getCircleColor(): CirclePaint['circle-color'] {
  return [
    'match',
    ['get', 'risk'],
    '0',
    RegionalRiskMycotoxinRiskLevelColors.NO_RISK,
    '1',
    RegionalRiskMycotoxinRiskLevelColors.LOW_RISK,
    '2',
    RegionalRiskMycotoxinRiskLevelColors.MEDIUM_RISK,
    '3',
    RegionalRiskMycotoxinRiskLevelColors.HIGH_RISK,
    '4',
    RegionalRiskMycotoxinRiskLevelColors.VERY_HIGH_RISK,
    /* other */ 'transparent'
  ];
}

export function useRenderMycotoxinMap<T extends GeoJsonProperties>({ map, data }: RenderMycotoxinMap<T>): RenderMap {
  const currentUnitSystem = useSelector((state: AppState) => state.uiState.global.currentUnitSystem);
  const [mapPopup] = useState(
    new mapboxgl.Popup({
      closeButton: false,
      offset: [30, -10],
      closeOnClick: true
    })
  );

  const segmentTracking = useSegmentTracking();

  const handleFieldPopupOpen = useCallback(() => {
    segmentTracking.track('Mycotoxin - click on a field');
  }, [segmentTracking]);

  const handleMapClick = useCallback(
    (event: mapboxgl.MapLayerMouseEvent) => {
      if (!map) return;
      if (!event.features) return;

      const feature = event.features[0];
      const coordinates = event.lngLat;

      if (!feature?.properties) return;

      const clusterCount = feature.properties.point_count as number;
      const callback: Parameters<GeoJSONSource['getClusterLeaves']>[3] = (_error, features) => {
        const popupNode = document.createElement('div');
        const root = createRoot(popupNode);
        root.render(
          <RegionalOverviewMycotoxinFieldsInfoPopupContainer
            currentUnitSystem={currentUnitSystem}
            mapboxPopup={mapPopup}
            data={features as Feature<Point, RegionalOverviewProperties>[]}
            clusterCount={clusterCount}
            onFieldPopupOpened={handleFieldPopupOpen}
          />
        );

        mapPopup.setLngLat(coordinates).setDOMContent(popupNode).addTo(map);
      };

      if (feature.properties.cluster) {
        const mapSource = map.getSource(POINTS_CLUSTER_DATA_SOURCE) as GeoJSONSource;
        mapSource.getClusterLeaves(feature.id as number, clusterCount, 0, callback);
      } else {
        const parsedFeature = {
          ...feature,
          properties: {
            ...feature.properties
          }
        };
        callback(null, [parsedFeature]);
      }
    },
    [mapPopup, map, currentUnitSystem, handleFieldPopupOpen]
  );

  const [hasRendered, setHasRendered] = useState(false);
  const handleRender = useCallback((): void => {
    if (!hasRendered) {
      setHasRendered(true);
    }
  }, [hasRendered]);

  const handleRecenterMap = useCallback(() => {
    if (!data?.features?.length) {
      return;
    }
    map?.fitBounds(
      bbox({
        type: 'FeatureCollection',
        features: data.features
      }) as LngLatBoundsLike,
      {
        padding: REGIONAL_OVERVIEW_MAP_PADDING
      }
    );
  }, [data, map]);

  useEffect(() => {
    if (!map) return () => undefined;

    map.addSource(POINTS_DATA_SOURCE, {
      type: 'geojson',
      cluster: false,
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
    map.addSource(POINTS_CLUSTER_DATA_SOURCE, {
      type: 'geojson',
      cluster: true,
      clusterMinPoints: 0,
      clusterRadius: 1,
      clusterMaxZoom: 24,
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    map.addLayer({
      id: POINTS_OVERVIEW_LAYER,
      type: 'circle',
      source: POINTS_DATA_SOURCE,
      maxzoom: 10,
      layout: {
        'circle-sort-key': ['to-number', ['get', 'risk']]
      },
      paint: {
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0.7, 8, 0.8],
        'circle-blur': 0.6,
        'circle-radius': {
          base: 1,
          stops: [
            [0, 2],
            [3, 4],
            [6, 6],
            [9, 9],
            [10, 12]
          ]
        },
        'circle-color': getCircleColor()
      }
    });

    map.addLayer({
      id: POINTS_LAYER,
      type: 'circle',
      source: POINTS_DATA_SOURCE,
      minzoom: 10,
      layout: {
        'circle-sort-key': ['to-number', ['get', 'risk']]
      },
      paint: {
        'circle-blur': 0.2,
        'circle-radius': {
          base: 1,
          stops: [
            [10, 8],
            [12, 10],
            [15, 12],
            [18, 15],
            [21, 18],
            [22, 20]
          ]
        },
        'circle-color': getCircleColor()
      }
    });

    map.addLayer({
      id: POINTS_CLUSTER_CIRCLE_LAYER,
      type: 'circle',
      source: POINTS_CLUSTER_DATA_SOURCE,
      minzoom: 10,
      paint: {
        'circle-color': ['case', ['has', 'point_count'], '#707374', 'transparent'],
        'circle-radius': ['case', ['has', 'point_count'], 20, 10],
        'circle-stroke-color': ['case', ['has', 'point_count'], '#FFFFFF', 'transparent'],
        'circle-stroke-width': 3,
        'circle-stroke-opacity': 0.5
      }
    });

    map.addLayer({
      id: POINTS_CLUSTER_COUNTER_LAYER,
      type: 'symbol',
      source: POINTS_CLUSTER_DATA_SOURCE,
      minzoom: 10,
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Regular'],
        'text-size': 18
      },
      paint: {
        'text-color': '#FFFFFF'
      }
    });

    return () => {
      map.removeLayer(POINTS_OVERVIEW_LAYER);
      map.removeLayer(POINTS_LAYER);
      map.removeLayer(POINTS_CLUSTER_COUNTER_LAYER);
      map.removeLayer(POINTS_CLUSTER_CIRCLE_LAYER);
      map.removeSource(POINTS_DATA_SOURCE);
      map.removeSource(POINTS_CLUSTER_DATA_SOURCE);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !data) return;
    (map.getSource(POINTS_DATA_SOURCE) as mapboxgl.GeoJSONSource).setData(data);
    (map.getSource(POINTS_CLUSTER_DATA_SOURCE) as mapboxgl.GeoJSONSource).setData(data);
    handleRecenterMap();
  }, [map, data, handleRecenterMap]);

  useEffect(() => {
    if (!map) return () => undefined;

    map.on('click', POINTS_CLUSTER_CIRCLE_LAYER, handleMapClick);
    return () => {
      map.off('click', POINTS_CLUSTER_CIRCLE_LAYER, handleMapClick);
    };
  }, [map, handleMapClick]);

  useEffect(() => {
    if (!map) return () => undefined;

    map.on('render', handleRender);
    return () => {
      map.off('render', handleRender);
    };
  }, [map, handleRender]);

  return {
    hasRendered
  };
}
